import { type FC, type FormEvent, Fragment, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import SearchIcon from '@crehana/icons/Search';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Loader, TextField } from '@crehana/ui';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import useLeadership from '@/views/AdminDashboard/hooks/useLeadership';
import useSearchCorpOrgs from '@/views/AdminDashboard/hooks/useSearchCorpOrgs';
import useSearchCorpUsers from '@/views/AdminDashboard/hooks/useSearchCorpUsers';
import useSearchOrganizationTeams from '@/views/AdminDashboard/hooks/useSearchOrganizationTeams';
import useSearchOrganizationUsers from '@/views/AdminDashboard/hooks/useSearchOrganizationUsers';

import type { BaseItemProps } from '../../types';
import { useContainerRef } from '../hooks';
import { EmptyCase, ListElement, ViewMoreResults } from './components';

const ITEMS_PER_FETCH = 3;
const { baseLighter } = colors;

const Search: FC<React.PropsWithChildren<BaseItemProps>> = ({ isDark }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isStudent } = useLeadership();
  const [searchKey, setSearchKey] = useState('');
  const { containerRef, setIsOpen, isOpen } = useContainerRef();
  const { slug: organizationSlug, isCorporation } = useAdminDashboard();

  const { loadingUsers: loadingUsersCorp, userResults: userResultsCorp } =
    useSearchCorpUsers({
      slug: organizationSlug,
      searchKey: isCorporation ? searchKey : '',
      itemsPerFetch: ITEMS_PER_FETCH,
    });

  const {
    loadingOrgs: loadingOrgsCorp,
    orgsResults: orgsResultsCorp,
    totalOrgsCount: totalOrgsCountCorp,
  } = useSearchCorpOrgs({
    searchKey: isCorporation ? searchKey : '',
    slug: organizationSlug,
    itemsPerFetch: ITEMS_PER_FETCH,
  });

  const { loadingUsers, userResults } = useSearchOrganizationUsers({
    slug: organizationSlug,
    searchKey: !isCorporation ? searchKey : '',
    itemsPerFetch: ITEMS_PER_FETCH,
  });

  const {
    loadingTeams,
    teamResults,
    totalOrgCount: totalTeamCount,
  } = useSearchOrganizationTeams({
    slug: organizationSlug,
    searchKey: !isCorporation ? searchKey : '',
    itemsPerFetch: ITEMS_PER_FETCH,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    router.push({
      pathname: `/org/${organizationSlug}/${
        isCorporation ? 'panel/corp' : 'panel'
      }/busqueda/`,
      query: { q: searchKey },
    });
  };

  const loading = isCorporation
    ? loadingUsersCorp || loadingOrgsCorp
    : loadingUsers || loadingTeams;

  const users = isCorporation ? userResultsCorp : userResults;

  const textColor = isDark
    ? 'tw-text-neutral-light-100'
    : 'tw-text-neutral-light-800';

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (isStudent) return <></>;

  return (
    <form onSubmit={onSubmit}>
      <div className="relative mr-16" ref={containerRef}>
        <TextField
          hint={clsx(
            t('SEARCH_HEADER_HINT_1'),
            isCorporation
              ? t('SEARCH_HEADER_HINT_2')
              : t('SEARCH_HEADER_HINT_3'),
          )}
          startAdornment={<SearchIcon color={baseLighter} size={24} />}
          chat
          darkMode={isDark}
          onChange={event => setSearchKey(event.target.value)}
          value={searchKey}
          onFocus={() => setIsOpen(!isOpen)}
          disabled={loadingTeams}
          textType="body4"
        />
        {isOpen && searchKey && (
          <div
            className={`absolute rounded-5 z-dialog w-full overflow-hidden ${
              isDark ? 'bg-base-main' : 'bg-white shadow-dropdown'
            }`}
          >
            {loading ? (
              <div className="flex justify-center items-center p-24">
                <Loader isWhite={isDark} />
              </div>
            ) : (
              <Fragment>
                {users.length > 0 && (
                  <div className="pt-24">
                    <p className={clsx(textColor, 'font-label mb-16 px-24')}>
                      {t('SEARCH_HEADER_USERS')}
                    </p>
                    {users.map(user => (
                      <ListElement
                        isDark={isDark}
                        key={user.id}
                        element={user}
                      />
                    ))}
                  </div>
                )}
                {teamResults.length > 0 && (
                  <div className="pt-24">
                    <p className={clsx(textColor, 'font-label mb-16 px-24')}>
                      {t('SEARCH_HEADER_TRACKS')}
                    </p>
                    {teamResults.map(team => (
                      <ListElement
                        isDark={isDark}
                        key={team.id}
                        element={team}
                      />
                    ))}
                  </div>
                )}
                {orgsResultsCorp.length > 0 && (
                  <div className="pt-24">
                    <p className={clsx(textColor, 'font-label mb-16 px-24')}>
                      {t('SEARCH_HEADER_ORGANIZATIONS')}
                    </p>
                    {orgsResultsCorp.map(org => (
                      <ListElement isDark={isDark} key={org.id} element={org} />
                    ))}
                  </div>
                )}

                {teamResults.length === 0 &&
                  users.length === 0 &&
                  orgsResultsCorp.length === 0 &&
                  searchKey && <EmptyCase />}

                {searchKey &&
                  (users.length > ITEMS_PER_FETCH ||
                    totalOrgsCountCorp > ITEMS_PER_FETCH ||
                    totalTeamCount > ITEMS_PER_FETCH) && (
                    <ViewMoreResults
                      isDark={isDark}
                      organizationSlug={organizationSlug}
                      searchKey={searchKey}
                    />
                  )}
              </Fragment>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export default Search;
